import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resetUserPassword } from '../../api/admin';
import Input from '../ui/Input';
import { useToast } from '../../context/ToastContext';
import { getFriendlyError } from '../../utils/friendlyError';

const ResetPasswordModal = ({ user, open, onClose }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const resetMut = useMutation({
    mutationFn: () => resetUserPassword(user._id, { password }),
    onSuccess: (res) => {
      showToast(res.message || t('admin.users.passwordResetSuccess'), 'success');
      setPassword('');
      setConfirmPassword('');
      onClose();
    },
    onError: (err) => showToast(getFriendlyError(err, t('admin.users.passwordResetFailed')), 'error'),
  });

  if (!open || !user) return null;

  const canSubmit = password.length >= 8 && password === confirmPassword;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
        <h2 className="text-lg font-semibold">{t('admin.users.resetPasswordTitle')}</h2>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
          {t('admin.users.resetPasswordSubtitle', {
            name: `${user.firstName} ${user.lastName}`,
          })}
        </p>

        <div className="mt-4 space-y-3">
          <Input
            label={t('admin.users.newPassword')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
          />
          <Input
            label={t('profile.confirmNewPassword')}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>

        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-zinc-300 dark:border-zinc-700 py-2 text-sm">
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={!canSubmit || resetMut.isPending}
            onClick={() => resetMut.mutate()}
            className="flex-1 rounded-lg bg-glow-600 py-2 text-sm text-white disabled:opacity-50"
          >
            {resetMut.isPending ? t('common.saving') : t('admin.users.resetPassword')}
          </button>
        </div>
      </div>
    </>
  );
};

export default ResetPasswordModal;
